import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import siteData from '../data/siteData.json';
import api from '../api/client';
import adminApi from '../api/adminClient';
import useAdminMode from '../hooks/useAdminMode';
import EditModal, { SectionEditBtn } from '../components/EditModal';
import './Home.css';

/* ── Animated counter hook ── */
function useCounter(target, duration = 2000, startCounting) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!startCounting) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, startCounting]);
  return count;
}

/* ── Stat item ── */
function StatItem({ stat, startCounting }) {
  const count = useCounter(stat.value, 2200, startCounting);
  return (
    <div className="stat-item">
      <div className="stat-item__value">
        {count.toLocaleString()}<span className="stat-item__unit">{stat.unit}</span>
      </div>
      <div className="stat-item__label">{stat.label}</div>
    </div>
  );
}

/* ── Intersection observer hook ── */
function useInView(threshold = 0.2) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ── Quick Contact Form ── */
function QuickContact() {
  const [form, setForm] = useState({ company: '', name: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.name || !form.phone) {
      alert('회사명, 성함, 연락처를 입력해 주세요.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/inquiries', { ...form, source: 'quick' });
      setSubmitted(true);
    } catch (err) {
      setError(err.message || '문의 접수 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="qc-success">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
        <h3>문의가 접수되었습니다!</h3>
        <p>담당자가 1 영업일 이내에 연락 드리겠습니다.</p>
        <button className="btn btn-primary" onClick={() => setSubmitted(false)}>새 문의 작성</button>
      </div>
    );
  }

  return (
    <form className="qc-form" onSubmit={handleSubmit}>
      {error && <div className="form-error">{error}</div>}
      <div className="qc-form__row">
        <div className="form-group">
          <label>회사명 <span className="required">*</span></label>
          <input name="company" value={form.company} onChange={handleChange} placeholder="주식회사 OO" />
        </div>
        <div className="form-group">
          <label>성함 <span className="required">*</span></label>
          <input name="name" value={form.name} onChange={handleChange} placeholder="홍길동" />
        </div>
        <div className="form-group">
          <label>연락처 <span className="required">*</span></label>
          <input name="phone" value={form.phone} onChange={handleChange} placeholder="010-0000-0000" />
        </div>
      </div>
      <div className="form-group">
        <label>문의 내용</label>
        <textarea name="message" value={form.message} onChange={handleChange} rows={3} placeholder="문의하실 내용을 간략히 적어주세요." />
      </div>
      <button type="submit" className="btn btn-accent qc-submit" disabled={submitting}>
        {submitting ? '신청 중...' : '무료 상담 신청하기'}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
      </button>
    </form>
  );
}

const heroSlides = [
  {
    title: '기업에는 최적의 인재를,',
    titleAlt: '인재에게는 최고의 내일을',
    sub: '20년 검증된 아웃소싱 전문 파트너, 주식회사 허브가 함께합니다.',
    bg: '/slide1.jpg',
  },
  {
    title: '전국 어디서나,',
    titleAlt: '유연하고 신속한 인력 공급',
    sub: '요청 후 48시간 이내 후보자 추천 — 인력 공백 없는 경영을 실현합니다.',
    bg: '/slide2.jpg',
  },
  {
    title: '헤드헌팅부터 생산 도급까지',
    titleAlt: '한 곳에서 해결하는 종합 솔루션',
    sub: '아웃소싱의 모든 영역을 하나의 파트너와 함께하는 경험, 허브가 드립니다.',
    bg: '/slide3.jpg',
  },
];

const defaultQuickContact = {
  title: '지금 바로 무료\n상담을 신청하세요',
  subtitle: '인력 문제는 빠를수록 좋습니다.\n전담 컨설턴트가 1 영업일 이내 연락드립니다.',
};

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [statsRef, statsInView] = useInView(0.3);
  const [company, setCompany] = useState(siteData.company);
  const [strengths, setStrengths] = useState(siteData.strengths);
  const [quickContact, setQuickContact] = useState(defaultQuickContact);
  const [modal, setModal] = useState(null);
  const { isAdmin } = useAdminMode();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    api.get('/company').then(setCompany).catch(() => {});
    api.get('/site-content/strengths').then(setStrengths).catch(() => {});
    api.get('/site-content/quick_contact').then(setQuickContact).catch(() => {});
  }, []);

  async function handleSave(key, value, setter) {
    try {
      await adminApi.put(`/site-content/${key}`, value);
      setter(value);
      setModal(null);
    } catch (err) {
      alert(err.message);
    }
  }

  const { stats, services } = siteData;

  return (
    <main>
      {/* ─── Hero ─── */}
      <section className="hero">
        {heroSlides.map((slide, i) => (
          <div
            key={i}
            className={`hero__slide ${i === currentSlide ? 'hero__slide--active' : ''}`}
            style={{
              backgroundImage: `url(${slide.bg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        ))}
        <div className="hero__overlay" />
        <div className="container hero__content">
          <div className="hero__badge">
            <span className="hero__badge-dot" />
            주식회사 허브 — 아웃소싱 전문 기업
          </div>
          <h1 className="hero__title">
            {heroSlides[currentSlide].title}<br />
            <em>{heroSlides[currentSlide].titleAlt}</em>
          </h1>
          <p className="hero__sub">{heroSlides[currentSlide].sub}</p>
          <div className="hero__actions">
            <Link to="/contact" className="btn btn-accent">
              무료 상담 신청
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </Link>
            <Link to="/careers" className="btn btn-outline">채용 공고 보기</Link>
          </div>
          <div className="hero__chips">
            <span>✓ 행사 지원</span>
            <span>✓ 헤드헌팅·채용대행</span>
            <span>✓ 생산·물류·도급</span>
          </div>
        </div>
        <div className="hero__dots">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              className={`hero__dot ${i === currentSlide ? 'hero__dot--active' : ''}`}
              onClick={() => setCurrentSlide(i)}
              aria-label={`슬라이드 ${i + 1}`}
            />
          ))}
        </div>
        <div className="hero__scroll">
          <span>스크롤</span>
          <div className="hero__scroll-line" />
        </div>
      </section>

      {/* ─── Why HUB ─── */}
      <section className="section section--gray">
        <div className="container">
          <div className="section-header">
            <span className="eyebrow">Why HUB</span>
            <h2>
              허브를 선택해야 하는 이유
              {isAdmin && <SectionEditBtn onClick={() => setModal({ key: 'strengths', title: '"허브를 선택해야 하는 이유" 편집', value: strengths, setter: setStrengths })} />}
            </h2>
            <p>20년간 1,200개 이상의 고객사와 함께한 경험으로 검증된 경쟁력입니다.</p>
            <div className="divider" />
          </div>
          <div className="strengths-grid">
            {strengths.map((s, i) => (
              <div key={s.id} className="strength-card fade-up visible" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="strength-card__icon">
                  <StrengthIcon name={s.icon} />
                </div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Services ─── */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <span className="eyebrow">Our Services</span>
            <h2>주요 사업 영역</h2>
            <p>기업과 인재를 연결하는 허브의 전문 서비스를 소개합니다.</p>
            <div className="divider" />
          </div>
          <div className="services-grid">
            {services.map(svc => (
              <Link key={svc.id} to="/services" className="service-card">
                <div className="service-card__header">
                  <div className="service-card__icon">
                    <ServiceIcon name={svc.icon} />
                  </div>
                  <h3>{svc.title}</h3>
                </div>
                <p>{svc.summary}</p>
                <ul className="service-card__features">
                  {svc.features.slice(0, 3).map((f, i) => (
                    <li key={i}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="service-card__cta">
                  자세히 보기
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Stats Counter (숨김 처리) ─── */}

      {/* ─── Quick Contact ─── */}
      <section className="section section--blue">
        <div className="container">
          <div className="qc-wrapper">
            <div className="qc-info">
              <span className="eyebrow" style={{ background: 'var(--blue-100)', color: 'var(--blue-400)', display: 'inline-block', padding: '0.3rem 1rem', borderRadius: '20px', marginBottom: '1rem', fontSize: '0.75rem', fontFamily: 'var(--font-en)', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Quick Contact</span>
              <h2 className="text-navy">
                {(quickContact.title || '').split('\n').map((line, i, arr) => (
                  <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
                ))}
                {isAdmin && <SectionEditBtn onClick={() => setModal({ key: 'quick_contact', title: 'Quick Contact 안내 문구 편집', value: quickContact, setter: setQuickContact })} />}
              </h2>
              <p className="text-muted" style={{ marginTop: '1rem' }}>
                {(quickContact.subtitle || '').split('\n').map((line, i, arr) => (
                  <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
                ))}
              </p>
              <ul className="qc-info__list">
                <li>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 10.8 19.79 19.79 0 01.06 2.18 2 2 0 012.03 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                  <span>{company.phone}</span>
                </li>
                <li>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  <span>{company.business_hours || company.businessHours}</span>
                </li>
                <li>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  <span>{company.email}</span>
                </li>
              </ul>
            </div>
            <div className="qc-form-wrapper">
              <QuickContact />
            </div>
          </div>
        </div>
      </section>

      {modal && (
        <EditModal
          title={modal.title}
          value={modal.value}
          onSave={(val) => handleSave(modal.key, val, modal.setter)}
          onClose={() => setModal(null)}
        />
      )}
    </main>
  );
}

/* ── Icon helpers ── */
function StrengthIcon({ name }) {
  const icons = {
    'map-pin': <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>,
    'zap': <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>,
    'shield': <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>,
    'file-text': <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></>,
  };
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      {icons[name] || <circle cx="12" cy="12" r="10"/>}
    </svg>
  );
}

function ServiceIcon({ name }) {
  const icons = {
    'users': <><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></>,
    'search': <><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>,
    'package': <><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></>,
    'briefcase': <><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></>,
  };
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      {icons[name] || <circle cx="12" cy="12" r="10"/>}
    </svg>
  );
}
